import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { useMutation } from 'urql';
import { useParams } from 'react-router-dom';
import {
  Diamond,
  KileTypeSKU,
} from '../components/orderContractor/pdf-shapes/protan/SheetWedgeShapeDrawing';
import {
  LinearRing2,
  LineSegment2,
  Point2,
} from '../domain/geometry/geometric-types';
import { kileSelector } from '../components/orderContractor/pdf-shapes/protan/kile-selector';
import { createMultipolygon } from '../components/orderContractor/shapes/util';
import { calculateSegmentLength } from '../domain/geometry/algorithms/util/line-segment';
import { toJstsMultiPolygon } from '../domain/geometry/algorithms/util/polygon';
import {
  CreateSheetShapeDocument,
  CreateOrderEntryFromTemplateDocument,
  BulkUpsertSparkelPropertiesDocument,
} from '../gql/graphql';
import { getSheetShapeUrn } from '../components/viewers/common/hooks/useSheetShapesManager';
import {
  findLongestAdjacentSegment,
  findSecondLongestParallelSegment,
  placePointsOnLines,
  calculatePolygonCenter,
  isClockwise,
  linearRing2ToLineSegments,
  addWedgesOnEachSide,
  calculateNormals,
  updateDiamondProperties as determineWedgesForLine,
} from '../components/orderContractor/pdf-shapes/protan/auto-generated-roofs';
import { updateWedgeProps } from './protan-special-handling';
interface AutogeneratedRoofContextProps {
  automaticDrainAndWedgesPlacement: boolean;
  setAutomaticDrainAndWedgesPlacement: (value: boolean) => void;
  projectId: string;
  orderId: string;
  handleSubmitDrains: (
    projectId: string,
    sheetId: string,
    sheetPageNumber: number
  ) => Promise<void>;
  handleSubmitWedges: (
    lineSegments: LineSegment2[],
    projectId: string,
    sheetId: string,
    sheetPageNumber: number,
    diamonds: Diamond[],
    kilteTypeSku: KileTypeSKU[]
  ) => Promise<void>;
  pointsOnLine1: Point2[];
  pointsOnLine2: Point2[];
  segments1: LineSegment2[];
  segments2: LineSegment2[];
  diamondsLine1: Diamond[];
  diamondsLine2: Diamond[];
  kilerLine1: KileTypeSKU[];
  kilerLine2: KileTypeSKU[];
  setPolygonData: (vertices: LinearRing2, unitScale: number) => void;
}

const AutogeneratedRoofContext = createContext<
  AutogeneratedRoofContextProps | undefined
>(undefined);

export const AutogeneratedRoofProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { projectId, orderId } = useParams<{
    projectId: string;
    orderId: string;
  }>() as {
    projectId: string;
    orderId: string;
  };
  const [createShapeResult, createShapeMutation] = useMutation(
    CreateSheetShapeDocument
  );

  const [
    automaticDrainAndWedgesPlacement,
    setAutomaticDrainAndWedgesPlacement,
  ] = useState(false);
  const [pointsOnLine1, setPointsOnLine1] = useState<Point2[]>([]);
  const [pointsOnLine2, setPointsOnLine2] = useState<Point2[]>([]);
  const [drainsOnLine1, setDrainsOnLine1] = useState<Point2[]>([]);
  const [drainsOnLine2, setDrainsOnLine2] = useState<Point2[]>([]);
  const [segments1, setSegments1] = useState<LineSegment2[]>([]);
  const [segments2, setSegments2] = useState<LineSegment2[]>([]);
  const [diamondsLine1, setDiamondsLine1] = useState<Diamond[]>([]);
  const [diamondsLine2, setDiamondsLine2] = useState<Diamond[]>([]);
  const [kilerLine1, setKilerLine1] = useState<KileTypeSKU[]>([]);
  const [kilerLine2, setKilerLine2] = useState<KileTypeSKU[]>([]);

  const [, createOrderEntryFromTemplate] = useMutation(
    CreateOrderEntryFromTemplateDocument
  );

  const [, bulkCreateSparkelProps] = useMutation(
    BulkUpsertSparkelPropertiesDocument
  );
  const handleCreateFromTemplateClick = useCallback(
    async (templateId: string, title: string, query: any, orderId: string) => {
      const input = {
        orderEntryTemplateId: templateId,
        orderId,
        title,
        color: '#FFFFFF',
        query,
        dbids: null,
      };
      await createOrderEntryFromTemplate({ input });
    },
    [createOrderEntryFromTemplate]
  );

  //Move this to state
  const movePoints60cmIn = (
    pointsLine1: Point2[],
    pointsLine2: Point2[],
    unitScale: number
  ): Point2[][] => {
    const moveDistance = 0.6 / unitScale; // 60 cm
    const newPointsLine1: Point2[] = [];
    const newPointsLine2: Point2[] = [];

    // Calculate the closest point on line2 for each point on line1 and vice versa
    const getClosestPoint = (point: Point2, points: Point2[]): Point2 => {
      let closestPoint = points[0];
      let minDistance = Number.MAX_VALUE;

      points.forEach((p) => {
        const distance = Math.sqrt(
          (p[0] - point[0]) * (p[0] - point[0]) +
            (p[1] - point[1]) * (p[1] - point[1])
        );

        if (distance < minDistance) {
          minDistance = distance;
          closestPoint = p;
        }
      });

      return closestPoint;
    };

    const normalizeVector = (vector: Point2): Point2 => {
      const [x, y] = vector;
      const length = Math.sqrt(x * x + y * y);
      return [x / length, y / length];
    };

    // Move points of Line1 towards Line2
    for (const point of pointsLine1) {
      const closestPoint = getClosestPoint(point, pointsLine2);
      const directionVector: Point2 = [
        closestPoint[0] - point[0],
        closestPoint[1] - point[1],
      ];
      const normalizedVector = normalizeVector(directionVector);
      const newPoint: Point2 = [
        point[0] + normalizedVector[0] * moveDistance,
        point[1] + normalizedVector[1] * moveDistance,
      ];
      newPointsLine1.push(newPoint);
    }

    // Move points of Line2 towards Line1
    for (const point of pointsLine2) {
      const closestPoint = getClosestPoint(point, pointsLine1);
      const directionVector: Point2 = [
        closestPoint[0] - point[0],
        closestPoint[1] - point[1],
      ];
      const normalizedVector = normalizeVector(directionVector);
      const newPoint: Point2 = [
        point[0] + normalizedVector[0] * moveDistance,
        point[1] + normalizedVector[1] * moveDistance,
      ];
      newPointsLine2.push(newPoint);
    }

    return [newPointsLine1, newPointsLine2];
  };

  const handleSubmitDrains = async (
    projectId: string,
    sheetId: string,
    sheetPageNumber: number
  ) => {
    [drainsOnLine1, drainsOnLine2].forEach((pointsOnLine) => {
      const pointSheetShapeInput = pointsOnLine.map((point) => ({
        sheetShape: {
          projectId,
          sheetId,
          urn: getSheetShapeUrn(projectId),
          sheetPageNumber,
          name: 'Drain',
          sheetShapePoint: {
            create: [{ point: { x: point[0], y: point[1] } }],
          },
        },
      }));
      pointSheetShapeInput.forEach(async (shapeInput) => {
        const result = await createShapeMutation({ input: shapeInput });
        if (result?.data?.createSheetShape?.sheetShape)
          bulkCreateSparkelProps({
            input: {
              projectId,
              dbIds: [
                {
                  modelUrn: result?.data?.createSheetShape?.sheetShape?.urn,
                  dbIds: [result?.data?.createSheetShape?.sheetShape?.dbId],
                },
              ],
              thePropertySet: 'OrderId',
              thePropertyValue: orderId,
            },
          });
      });
    });
  };

  const handleSubmitWedges = async (
    lineSegments: LineSegment2[],
    projectId: string,
    sheetId: string,
    sheetPageNumber: number,
    diamonds: Diamond[],
    kilteTypeSku: KileTypeSKU[]
  ) => {
    const lineInputs = lineSegments.map((lineSegment) => ({
      sheetShape: {
        projectId,
        sheetId,
        urn: getSheetShapeUrn(projectId),
        sheetPageNumber,
        name: 'Wedge',
        sheetShapeLine: {
          create: [
            {
              points: [
                { x: lineSegment[0][0], y: lineSegment[0][1] },
                { x: lineSegment[1][0], y: lineSegment[1][1] },
              ],
            },
          ],
        },
      },
    }));
    lineInputs.forEach(async (shapeInput, index) => {
      const result = await createShapeMutation({ input: shapeInput });
      if (result.data?.createSheetShape?.sheetShape) {
        updateWedgeProps(
          result?.data,
          diamonds[index],
          kilteTypeSku[index],
          bulkCreateSparkelProps,
          projectId
        );
        bulkCreateSparkelProps({
          input: {
            projectId,
            dbIds: [
              {
                modelUrn: result?.data?.createSheetShape?.sheetShape?.urn,
                dbIds: [result?.data?.createSheetShape?.sheetShape?.dbId],
              },
            ],
            thePropertySet: 'OrderId',
            thePropertyValue: orderId,
          },
        });
      }
    });
  };

  const setPolygonData = (vertices: LinearRing2, unitScale: number) => {
    const edgeDistanceFactor = 0.5;

    const polygon = toJstsMultiPolygon(createMultipolygon([vertices]));
    const size = polygon.getArea() * unitScale * unitScale;
    const antallSluk = Math.max(size / 300, 2);
    const normalizedVertices = vertices;

    const { longestSegment } = findLongestAdjacentSegment(normalizedVertices);
    const { secondLongestSegment } = findSecondLongestParallelSegment(
      normalizedVertices,
      longestSegment
    );

    const { pointsOnLine1, pointsOnLine2 } = placePointsOnLines(
      antallSluk,
      longestSegment,
      secondLongestSegment,
      edgeDistanceFactor,
      unitScale
    );

    if (pointsOnLine1.length === 0 || pointsOnLine2.length === 0) {
      return;
    }

    const center = calculatePolygonCenter(normalizedVertices);
    const clockwise = isClockwise(normalizedVertices);
    const normalsLine1 = calculateNormals(pointsOnLine1, clockwise);
    const normalsLine2 = calculateNormals(pointsOnLine2, clockwise);

    const segmentsLine1 = linearRing2ToLineSegments(pointsOnLine1);
    const segmentsLine2 = linearRing2ToLineSegments(pointsOnLine2);

    const segments1: LineSegment2[] = [[longestSegment[0], pointsOnLine1[0]]];
    const endSegment: LineSegment2 = [
      pointsOnLine1[pointsOnLine1.length - 1],
      longestSegment[longestSegment.length - 1],
    ];

    segments1.push(...segmentsLine1, endSegment);

    const segments2: LineSegment2[] = [
      [secondLongestSegment[0], pointsOnLine2[0]],
    ];
    const endSegment2: LineSegment2 = [
      pointsOnLine2[pointsOnLine2.length - 1],
      secondLongestSegment[secondLongestSegment.length - 1],
    ];

    segments2.push(...segmentsLine2, endSegment2);

    let diamondsLine1 = determineWedgesForLine(
      normalsLine1,
      pointsOnLine1,
      clockwise,
      center
    );
    let diamondsLine2 = determineWedgesForLine(
      normalsLine2,
      pointsOnLine2,
      clockwise,
      center
    );

    diamondsLine1 = addWedgesOnEachSide(diamondsLine1);
    diamondsLine2 = addWedgesOnEachSide(diamondsLine2);

    const kilerLine1 = segments1.map(
      (segment, index) =>
        (kileSelector(
          diamondsLine1[index],
          calculateSegmentLength(segment) * unitScale
        ) as KileTypeSKU) ?? KileTypeSKU.INVALID_KILE
    );
    const kilerLine2 = segments2.map(
      (segment, index) =>
        (kileSelector(
          diamondsLine2[index],
          calculateSegmentLength(segment) * unitScale
        ) as KileTypeSKU) ?? KileTypeSKU.INVALID_KILE
    );

    const drainPoints = movePoints60cmIn(
      pointsOnLine1,
      pointsOnLine2,
      unitScale
    );
    setDrainsOnLine1(drainPoints[0]);
    setDrainsOnLine2(drainPoints[1]);
    setPointsOnLine1(pointsOnLine1);
    setPointsOnLine2(pointsOnLine2);
    setSegments1(segments1);
    setSegments2(segments2);
    setDiamondsLine1(diamondsLine1);
    setDiamondsLine2(diamondsLine2);
    setKilerLine1(kilerLine1);
    setKilerLine2(kilerLine2);
  };

  return (
    <AutogeneratedRoofContext.Provider
      value={{
        automaticDrainAndWedgesPlacement,
        setAutomaticDrainAndWedgesPlacement,
        projectId,
        orderId,
        handleSubmitDrains,
        handleSubmitWedges,
        pointsOnLine1,
        pointsOnLine2,
        segments1,
        segments2,
        diamondsLine1,
        diamondsLine2,
        kilerLine1,
        kilerLine2,
        setPolygonData,
      }}
    >
      {children}
    </AutogeneratedRoofContext.Provider>
  );
};

export const useAutoGeneratedRoof = (): AutogeneratedRoofContextProps => {
  const context = useContext(AutogeneratedRoofContext);
  if (!context) {
    throw new Error('usePolygon must be used within a PolygonProvider');
  }
  return context;
};
